/**
 * Route permissions configuration
 * Maps route paths to allowed roles
 */
import { CLASS_SECTIONS, FAMILY_SECTIONS, TEACHING_SECTIONS } from '../components/Sidebar/contextNav';

const CLASS_ROUTE = '/app/:schoolSlug/years/:yearSlug/courses/:courseSlug/classes/:classOfferingId';
const CLASS_ROLES = ['PRIMARY_ADMIN', 'ADMIN', 'TEACHER'];

const TEACHING_ROUTE = '/app/:schoolSlug/years/:yearSlug/teaching';

/**
 * Sections are routes of their own, and matchRoute compares segment counts, so
 * each one needs its own entry. Generating them from the same lists the sidebar
 * and router use means a new section cannot ship as an accidental "Access
 * Denied". Admin-only sections are gated here too, so hiding a link from
 * teachers and refusing them the URL stay one decision.
 */
function sectionPermissions(base: string, sections: readonly { path: string; adminOnly?: boolean }[]) {
  return Object.fromEntries(
    sections
      .filter((section) => section.path)
      .map((section) => [
        `${base}/${section.path}`,
        section.adminOnly ? CLASS_ROLES.filter((role) => role !== 'TEACHER') : CLASS_ROLES,
      ])
  );
}

const VIEW_AS_FAMILY_ROUTE = '/app/:schoolSlug/view-as-family/:familyId';
const ADMIN_ROLES = ['PRIMARY_ADMIN', 'ADMIN'];

/**
 * "View as family" mounts the family's own pages, so every page a parent has is
 * a route an admin can reach here. Generated from the same list the sidebar
 * draws, so adding a page to the family portal cannot leave its view-as twin
 * behind an "Access Denied".
 */
const viewAsFamilyPermissions = {
  [VIEW_AS_FAMILY_ROUTE]: ADMIN_ROLES,
  [`${VIEW_AS_FAMILY_ROUTE}/students/:personId`]: ADMIN_ROLES,
  [`${VIEW_AS_FAMILY_ROUTE}/years/:yearSlug/enroll`]: ADMIN_ROLES,
  [`${VIEW_AS_FAMILY_ROUTE}/years/:yearSlug/enroll/:itemId`]: ADMIN_ROLES,
  ...Object.fromEntries(
    FAMILY_SECTIONS
      .filter((section) => section.path)
      .map((section) => [`${VIEW_AS_FAMILY_ROUTE}/${section.path}`, ADMIN_ROLES])
  ),
};

const classSectionPermissions = {
  ...sectionPermissions(CLASS_ROUTE, CLASS_SECTIONS),
  ...sectionPermissions(TEACHING_ROUTE, TEACHING_SECTIONS),
};

export const routePermissions: Record<string, string[]> = {
  ...classSectionPermissions,
  '/app/:schoolSlug/dashboard': ['PRIMARY_ADMIN', 'ADMIN', 'PRIMARY_FAMILY_MEMBER'],
  '/app/:schoolSlug/families': ['PRIMARY_ADMIN', 'ADMIN'],
  '/app/:schoolSlug/families/:familySlug': ['PRIMARY_ADMIN', 'ADMIN'],
  '/app/:schoolSlug/families/:familySlug/enrollment': ['PRIMARY_ADMIN', 'ADMIN'],
  '/app/:schoolSlug/families/:familySlug/billing': ['PRIMARY_ADMIN', 'ADMIN'],
  '/app/:schoolSlug/families/:familySlug/enrollment-packet/:familyEnrollmentId': ['PRIMARY_ADMIN', 'ADMIN'],
  '/app/:schoolSlug/families/:familySlug/tuition-agreement/:familyEnrollmentId': ['PRIMARY_ADMIN', 'ADMIN'],
  // A parent reaches their own child here; the server decides whose record they
  // may actually load, so a wrong id returns nothing rather than someone else's.
  '/app/:schoolSlug/students/:personId': ['PRIMARY_ADMIN', 'ADMIN', 'PRIMARY_FAMILY_MEMBER'],
  '/app/:schoolSlug/students/:personId/enrollment': ['PRIMARY_ADMIN', 'ADMIN'],
  '/app/:schoolSlug/students/:personId/grades': ['PRIMARY_ADMIN', 'ADMIN'],
  // Settings is where everyone signs out and switches school, so every role can
  // open it. Which sections they get is decided inside the page: an
  // administrator sees the school's configuration, and nobody else does.
  '/app/:schoolSlug/settings': ['PRIMARY_ADMIN', 'ADMIN', 'TEACHER', 'PRIMARY_FAMILY_MEMBER'],
  '/app/:schoolSlug/settings/:section': ['PRIMARY_ADMIN', 'ADMIN', 'TEACHER', 'PRIMARY_FAMILY_MEMBER'],
  '/app/:schoolSlug/staff': ['PRIMARY_ADMIN', 'ADMIN'],
  '/app/:schoolSlug/staff/:staffMemberId': ['PRIMARY_ADMIN', 'ADMIN'],
  '/app/:schoolSlug/years/:yearSlug': ['PRIMARY_ADMIN', 'ADMIN', 'PRIMARY_FAMILY_MEMBER'],
  '/app/:schoolSlug/years/:yearSlug/enrollments': ['PRIMARY_ADMIN', 'ADMIN'],
  '/app/:schoolSlug/years/:yearSlug/invoices': ['PRIMARY_ADMIN', 'ADMIN'],
  '/app/:schoolSlug/years/:yearSlug/courses': ['PRIMARY_ADMIN', 'ADMIN'],
  '/app/:schoolSlug/years/:yearSlug/schedule': ['PRIMARY_ADMIN', 'ADMIN'],
  '/app/:schoolSlug/years/:yearSlug/attendance': ['PRIMARY_ADMIN', 'ADMIN'],
  '/app/:schoolSlug/years/:yearSlug/attendance-report': ['PRIMARY_ADMIN', 'ADMIN'],
  '/app/:schoolSlug/years/:yearSlug/attendance/:scheduleBlockId': ['PRIMARY_ADMIN', 'ADMIN', 'TEACHER'],
  '/app/:schoolSlug/years/:yearSlug/courses/:courseSlug': ['PRIMARY_ADMIN', 'ADMIN'],
  [CLASS_ROUTE]: CLASS_ROLES,
  '/app/:schoolSlug/years/:yearSlug/teaching': ['PRIMARY_ADMIN', 'ADMIN', 'TEACHER'],
  '/app/:schoolSlug/enrollments': ['PRIMARY_FAMILY_MEMBER'],
  '/app/:schoolSlug/invoices': ['PRIMARY_FAMILY_MEMBER'],
  '/app/:schoolSlug/family': ['PRIMARY_FAMILY_MEMBER'],
  '/app/:schoolSlug/household': ['PRIMARY_FAMILY_MEMBER'],
  '/app/:schoolSlug/enrollment': ['PRIMARY_FAMILY_MEMBER'],
  // A family's year and its checklist steps. The page shows only a year the
  // family has, and the server gives a step only to a guardian of its family
  // while the year is enrolling or enrolled (the office reaches these through
  // view as family, above).
  '/app/:schoolSlug/years/:yearSlug/enroll': ['PRIMARY_FAMILY_MEMBER'],
  '/app/:schoolSlug/years/:yearSlug/enroll/:itemId': ['PRIMARY_FAMILY_MEMBER'],
  ...viewAsFamilyPermissions,
};

export function canAccessRoute(routePath: string, role: string): boolean {
  const allowedRoles = routePermissions[routePath];
  if (!allowedRoles) {
    return false;
  }
  return allowedRoles.includes(role);
}

export function matchRoute(pattern: string, path: string): boolean {
  const patternParts = pattern.split('/');
  const pathParts = path.split('/');

  if (patternParts.length !== pathParts.length) {
    return false;
  }

  for (let i = 0; i < patternParts.length; i++) {
    if (patternParts[i].startsWith(':')) {
      continue;
    }
    if (patternParts[i] !== pathParts[i]) {
      return false;
    }
  }

  return true;
}

export function findMatchingRoute(path: string): string | null {
  for (const pattern of Object.keys(routePermissions)) {
    if (matchRoute(pattern, path)) {
      return pattern;
    }
  }
  return null;
}
